import { z } from 'zod';
import { Telemetry } from './Telemetry';

export const EventMessage = z.object({
  event: z.string(),
  data: z.unknown()
});

type WSCallback = {
  fn: (data: unknown) => void;
  shouldRemove: boolean;
};

interface WebSocketClientArgs {
  url: string;
  maxReconnectAttempts?: number;
  initialReconnectTimeout?: number;
}

export class WebSocketClient {
  private socket: WebSocket;
  private events: { [key: string]: WSCallback[] } = {};
  private onCloseCallbacks: (() => void)[] = [];
  private onOpenCallbacks: (() => void)[] = [];
  private onErrorCallbacks: ((error: Event) => void)[] = [];
  private reconnectTimeouts: number[] = [];
  private maxReconnectAttempts: number;
  private initialReconnectTimeout: number;

  constructor({
    url,
    maxReconnectAttempts = 10,
    initialReconnectTimeout = 1
  }: WebSocketClientArgs) {
    this.socket = this.connect(url);
    this.maxReconnectAttempts = maxReconnectAttempts;
    this.initialReconnectTimeout = initialReconnectTimeout;
  }

  connect(url: string) {
    const socket = new WebSocket(url);
    socket.onopen = () => {
      Telemetry.capture('websocket_connected', {
        url
      });
      this.reconnectTimeouts = [];
      for (const cb of this.onOpenCallbacks) {
        cb();
      }
    };

    socket.addEventListener('error', (error) => {
      for (const cb of this.onErrorCallbacks) {
        Telemetry.captureException(new Error('WebSocket error'), {
          url
        });
        cb(error);
      }
    });

    socket.onclose = () => {
      Telemetry.capture('websocket_closed', {
        url
      });
      if (this.reconnectTimeouts.length === 0) {
        this.reconnectTimeouts.push(this.initialReconnectTimeout);
      } else if (this.reconnectTimeouts.length < this.maxReconnectAttempts) {
        const newTimeout =
          (this.reconnectTimeouts[this.reconnectTimeouts.length - 1] ??
            this.initialReconnectTimeout) * 2;
        this.reconnectTimeouts.push(newTimeout);
      }
      if (
        this.socket.readyState === WebSocket.CLOSED &&
        this.reconnectTimeouts.length < this.maxReconnectAttempts
      ) {
        const time =
          this.reconnectTimeouts[this.reconnectTimeouts.length - 1] ??
          this.initialReconnectTimeout;

        setTimeout(() => {
          this.socket = this.connect(this.socket.url);
        }, time * 1000);
      }
      // check if already notified listeners
      if (this.reconnectTimeouts.length <= 1) {
        for (const cb of this.onCloseCallbacks) {
          cb();
        }
      }
    };
    socket.onmessage = ({ data }) => {
      const event = EventMessage.parse(JSON.parse(data));
      const callbacks = this.events[event.event];
      if (!callbacks) {
        return;
      }
      const toRemove: number[] = [];
      callbacks.forEach((cb, index) => {
        cb.fn(event.data);
        if (cb.shouldRemove) {
          toRemove.push(index);
        }
      });

      // reverse loop to avoid index shift
      for (let i = toRemove.length - 1; i >= 0; i--) {
        const index = toRemove[i];
        if (index) {
          callbacks.splice(index, 1);
        }
      }
    };
    return socket;
  }

  onMessage(event: string, callback: (data: unknown) => void) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event]?.push({ fn: callback, shouldRemove: false });

    return () => {
      this.events[event] =
        this.events[event]?.filter((cb) => cb.fn !== callback) ?? [];
    };
  }

  get readyState() {
    return this.socket.readyState;
  }

  onOpen(callback: () => void) {
    this.onOpenCallbacks.push(callback);
    return () => {
      this.onOpenCallbacks = this.onOpenCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onError(cb: (event: Event) => void) {
    this.onErrorCallbacks.push(cb);
    console.error('onError', cb);
    return () => {
      this.onErrorCallbacks = this.onErrorCallbacks.filter(
        (callback) => callback !== cb
      );
    };
  }

  onClose(callback: () => void) {
    this.onCloseCallbacks.push(callback);
    return () => {
      this.onCloseCallbacks = this.onCloseCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  onMessageOnce(event: string, callback: (data: unknown) => void) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event]?.push({ fn: callback, shouldRemove: true });
  }

  sendMessage(msg: string | object) {
    if (this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    if (typeof msg === 'object') {
      this.socket.send(JSON.stringify(msg));
    } else {
      this.socket.send(msg);
    }
  }

  close() {
    this.socket.close();
  }
}
